import {
	CodelistHistoryEventType,
	CodelistRecordChangedEvent,
	useGetCodelistRecordHistoryQuery
} from './useGetCodelistRecordHistoryQuery';
import { useProjectId } from '../../../hooks/useProjectId';
import { useLanguage } from '../../../hooks/useLanguage';
import { DateTime } from 'luxon';
import { useGetNamesFromUserIdsQuery } from '../../../entry-form-v2/data/useGetNamesFromUserIdsQuery';
import { Skeleton } from '../../../entry-form-v2/component/Skeleton';
import { CodelistIcons } from '../../icons/codelist-icons';
import { CodelistRecord, CodelistRecordKey } from '../../domain/codelistRecordSchema';
import { CODELIST_SIDEBAR_DICTIONARY } from '../Dictionary';
import { Tooltip } from '../display/Tooltip';
import { LanguageType } from '../../../../types';
import { FilePlus, Import, Pencil } from 'lucide-react';

export const ChangeHistoryTab = ({ originalEntryId }: { originalEntryId: string }) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];
	const projectId = useProjectId();

	const codelistRecordHistoryQuery = useGetCodelistRecordHistoryQuery({
		projectId,
		originalEntryId
	});

	const hasUserNames = (codelistRecordHistoryQuery.data?.events.length ?? 0) > 0;

	const userNamesByUserIdQuery = useGetNamesFromUserIdsQuery(
		{
			userIds: codelistRecordHistoryQuery.data?.events.map(event => event.userId) ?? []
		},
		{
			enabled: !!hasUserNames
		}
	);

	const isLoading = codelistRecordHistoryQuery.isLoading || userNamesByUserIdQuery.isLoading;

	if (!codelistRecordHistoryQuery.data && isLoading) {
		return <LoadingView />;
	}

	if (!codelistRecordHistoryQuery.data) {
		return null;
	}

	return (
		<ul className="flex flex-col gap-4 text-base">
			{codelistRecordHistoryQuery.data.events.map(event => (
				<li key={event.createdAt} className="p-4 border border-gray-200 rounded-lg">
					<div className="flex flex-row justify-between mb-4">
						<div className="flex flex-row gap-2 items-center font-bold">
							<EventTypeIcon eventType={event.eventType} />

							{userNamesByUserIdQuery.data?.namesFromUserIds[event.userId]
								? translation.eventTypeTitle[event.eventType](
										userNamesByUserIdQuery.data?.namesFromUserIds[event.userId]
								  )
								: translation.unknownUser}
						</div>

						<div>
							{DateTime.fromISO(event.createdAt).toLocaleString(
								DateTime.DATETIME_MED
							)}
						</div>
					</div>

					<EventPreview event={event} />
				</li>
			))}
		</ul>
	);
};

const EventPreview = (props: { event: CodelistRecordChangedEvent }) => {
	if (
		props.event.eventType === 'IMPORT_BULK_RECORDS' ||
		props.event.eventType === 'PARTIAL_IMPORT_RECORDS'
	) {
		if (props.event.metadata.records.length !== 1) {
			console.error('Expected 1 record, got', props.event.metadata.records.length);
		}

		const event = props.event.metadata.records[0];

		return <EventEntries record={event} />;
	}

	return <EventEntries record={props.event.metadata.record} />;
};

const EventEntries = ({ record }: { record: CodelistRecord }) => {
	return (
		<>
			{Object.entries(record)
				.filter(([key, value]) => value !== '' && value !== undefined && key !== 'id')
				.map(([key, value]) => (
					<RecordItem key={key} codelistKey={key as CodelistRecordKey} value={value} />
				))}
		</>
	);
};

const EventTypeIcon = (props: { eventType: CodelistHistoryEventType }) => {
	let Icon = null;

	switch (props.eventType) {
		case 'UPDATE_RECORD':
			Icon = Pencil;
			break;
		case 'IMPORT_BULK_RECORDS':
			Icon = Import;
			break;
		case 'PARTIAL_IMPORT_RECORDS':
			Icon = Import;
			break;
		case 'CREATE_RECORD':
			Icon = FilePlus;
			break;
	}

	return <Icon className="size-7" />;
};

const RecordItem = ({
	codelistKey,
	value
}: {
	codelistKey: keyof typeof CodelistIcons;
	value?: string;
}) => {
	const Icon = CodelistIcons[codelistKey];
	const language = useLanguage();

	return (
		<div className="flex flex-row gap-2 items-center">
			<Tooltip content={CODELIST_SIDEBAR_DICTIONARY[language][codelistKey].label}>
				<Icon className="size-6" />
			</Tooltip>

			<div>{value}</div>
		</div>
	);
};

const LoadingView = () => (
	<ul className="flex flex-col gap-4">
		{new Array(6).fill(null).map((_, index) => (
			<li key={index} className="p-4 border border-gray-200 rounded-lg">
				<Skeleton className="w-40 h-6 rounded-md" />
				<Skeleton className="w-48 h-4 mt-2 rounded-md" />
			</li>
		))}
	</ul>
);

const DICTIONARY = {
	[LanguageType.English]: {
		unknownUser: 'Unknown user',
		eventTypeTitle: {
			UPDATE_RECORD: (name: string) => `${name} made changes`,
			CREATE_RECORD: (name: string) => `${name} created`,
			IMPORT_BULK_RECORDS: (name: string) => `${name} imported`,
			PARTIAL_IMPORT_RECORDS: (name: string) => `${name} imported`
		}
	},
	[LanguageType.Norwegian]: {
		unknownUser: 'Ukjent bruker',
		eventTypeTitle: {
			UPDATE_RECORD: (name: string) => `${name} oppdaterte`,
			CREATE_RECORD: (name: string) => `${name} opprettet`,
			IMPORT_BULK_RECORDS: (name: string) => `${name} importerte`,
			PARTIAL_IMPORT_RECORDS: (name: string) => `${name} importerte`
		}
	}
};
